import type { User } from "firebase/auth";

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

export interface TopClientRow {
  rank: number;
  clientId: string;
  clientCode?: string | null;
  rif?: string | null;
  businessName?: string | null;
  legalName?: string | null;
  clientName?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  officePhone?: string | null;
  address?: string | null;
  city?: string | null;
  municipality?: string | null;
  zone?: string | null;
  fiscalAddress?: string | null;
  shippingAddress?: string | null;
  referencePoint?: string | null;
  creatorSalesPerson?: string | null;
  creatorName?: string | null;
  assignedSalesRep?: string | null;
  assignedSalesRepName?: string | null;
  responsible?: string | null;
  customerType?: string | null;
  salesZoneName?: string | null;
  active?: boolean | null;
  website?: string | null;
  orderCount: number;
  revenueWithTax: number;
  netRevenue: number;
  averageOrderValue: number;
  lastOrderDate?: string | null;
}

export interface TopClientsReport {
  organizationId: string;
  organizationName: string;
  dateFrom: string;
  dateTo: string;
  periodLabel: string;
  generatedAt: string;
  currency: string;
  totalClients: number;
  totalRevenueWithTax: number;
  totalNetRevenue: number;
  totalOrderCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  clients: TopClientRow[];
}

export type TopClientsPeriodQuery =
  | { mode: "days"; days: number }
  | { mode: "month"; year: number; month: number }
  | { mode: "range"; dateFrom: string; dateTo: string };

export interface TopClientsFetchParams {
  query: TopClientsPeriodQuery;
  limit?: number;
  offset?: number;
}

function buildPeriodParams(
  query: TopClientsPeriodQuery
): Record<string, string | number> {
  if (query.mode === "days") {
    return { days: query.days };
  }
  if (query.mode === "month") {
    return { year: query.year, month: query.month };
  }
  return { dateFrom: query.dateFrom, dateTo: query.dateTo };
}

async function parseError(response: Response): Promise<string> {
  const data = (await response.json().catch(() => null)) as unknown;
  if (
    data &&
    typeof data === "object" &&
    "detail" in data &&
    typeof (data as { detail?: unknown }).detail === "string"
  ) {
    return (data as { detail: string }).detail;
  }
  return `Backend error (${response.status}).`;
}

export const fetchOrganizationTopClients = async (
  user: User,
  organizationId: string,
  params: TopClientsFetchParams
): Promise<TopClientsReport> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const search = new URLSearchParams();
  const period = buildPeriodParams(params.query);
  Object.entries(period).forEach(([key, value]) => {
    search.set(key, String(value));
  });
  search.set("limit", String(params.limit ?? 25));
  search.set("offset", String(params.offset ?? 0));

  const response = await fetch(
    `${baseUrl}/api/v1/platform/organizations/${encodeURIComponent(
      organizationId
    )}/top-clients?${search.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return (await response.json()) as TopClientsReport;
};

/** Fetch every ranked page (capped) for Excel/marketing export. */
export const fetchAllOrganizationTopClients = async (
  user: User,
  organizationId: string,
  query: TopClientsPeriodQuery,
  pageSize = 200,
  maxRows = 20000
): Promise<TopClientsReport> => {
  const first = await fetchOrganizationTopClients(user, organizationId, {
    query,
    limit: pageSize,
    offset: 0,
  });
  const clients = [...first.clients];
  let offset = clients.length;
  let hasMore = first.hasMore;

  while (hasMore && clients.length < maxRows) {
    const page = await fetchOrganizationTopClients(user, organizationId, {
      query,
      limit: Math.min(pageSize, maxRows - clients.length),
      offset,
    });
    if (!page.clients.length) {
      break;
    }
    clients.push(...page.clients);
    offset += page.clients.length;
    hasMore = page.hasMore;
  }

  return {
    ...first,
    clients,
    limit: clients.length,
    offset: 0,
    hasMore: false,
  };
};
